import React, { useState, useEffect } from 'react';
import {
  UserAccount,
  FinancialTransaction,
  FestivalPledge,
  ExpenseRequisition,
  DepartmentBudget,
  FinancialAsset,
  CHART_OF_ACCOUNTS
} from '../../types';
import {
  getFinancialStore,
  saveFinancialStore,
  syncRaffleRevenueToFinancial,
  FinancialDataStore
} from '../../utils/financialStorage';
import { recordAuditLog } from '../../utils/auditStorage';

interface FinancialManagementModuleProps {
  currentUser?: UserAccount;
}

type FinancialTab =
  | 'dashboard'
  | 'transactions'
  | 'pledges'
  | 'requisitions'
  | 'budgets'
  | 'statements'
  | 'governance';

export const FinancialManagementModule: React.FC<FinancialManagementModuleProps> = ({ currentUser }) => {
  const [store, setStore] = useState<FinancialDataStore>(() => getFinancialStore());
  const [activeTab, setActiveTab] = useState<FinancialTab>('dashboard');

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [pledgeFilter, setPledgeFilter] = useState<string>('all');
  const [reqStatusFilter, setReqStatusFilter] = useState<string>('all');

  // Modals
  const [isNewTxModalOpen, setIsNewTxModalOpen] = useState(false);
  const [isNewPledgeModalOpen, setIsNewPledgeModalOpen] = useState(false);
  const [isRetirePledgeModalOpen, setIsRetirePledgeModalOpen] = useState(false);
  const [selectedPledgeToRetire, setSelectedPledgeToRetire] = useState<FestivalPledge | null>(null);
  const [isNewReqModalOpen, setIsNewReqModalOpen] = useState(false);
  const [selectedReqForDetail, setSelectedReqForDetail] = useState<ExpenseRequisition | null>(null);

  // Form states
  const [newTx, setNewTx] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    type: 'revenue' as FinancialTransaction['type'],
    category: 'Donations',
    accountCode: '40-100-001',
    payerOrPayee: '',
    paymentMethod: 'bank_transfer' as FinancialTransaction['paymentMethod'],
    referenceNo: '',
    notes: '',
  });

  const [newPledge, setNewPledge] = useState({
    pledgerName: '',
    titleOrOffice: '',
    villageOrChapter: 'Jattu-Uzairue',
    pledgerType: 'royal_chief' as FestivalPledge['pledgerType'],
    amountPledged: '',
    purpose: '',
    contactEmail: '',
    contactPhone: '',
    dueDate: '',
  });

  const [retireAmount, setRetireAmount] = useState('');
  const [retireMethod, setRetireMethod] = useState('Bank Transfer');
  const [retireBankRef, setRetireBankRef] = useState('');

  const [newReq, setNewReq] = useState({
    title: '',
    department: 'Logistics & Stage' as ExpenseRequisition['department'],
    requestedBy: currentUser?.name || 'Committee Scribe',
    amount: '',
    description: '',
    accountCode: '50-100-001',
    priority: 'normal' as ExpenseRequisition['priority'],
    beneficiaryName: '',
    bankAccountDetails: '',
  });

  const [activeGovernanceRole, setActiveGovernanceRole] = useState<'bot_member' | 'mc_executive' | 'financial_scribe'>(
    currentUser?.role === 'admin' ? 'bot_member' : 'mc_executive'
  );

  // Sync state with local storage & events
  useEffect(() => {
    const handleStorageUpdate = () => {
      setStore(getFinancialStore());
    };
    window.addEventListener('uzairue_financial_data_updated', handleStorageUpdate);
    return () => {
      window.removeEventListener('uzairue_financial_data_updated', handleStorageUpdate);
    };
  }, []);

  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);

  const handleBulkPrintReceipts = () => {
    if (selectedTransactionIds.length === 0) {
      alert('Please select at least one transaction in the register to generate bulk receipts.');
      return;
    }
    const selectedTxs = store.transactions.filter(t => selectedTransactionIds.includes(t.id));
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>BULK OFFICIAL TREASURY RECEIPTS - UZAIRUE KINGDOM</title>
          <style>
            body { font-family: 'Georgia', serif; margin: 20px; color: #111; line-height: 1.5; }
            .receipt-page { border: 6px double #0F5132; padding: 25px; background: #fffdfa; position: relative; margin-bottom: 40px; page-break-after: always; }
            .header { text-align: center; border-bottom: 2px solid #78350F; padding-bottom: 10px; margin-bottom: 15px; }
            .title { font-size: 18px; font-weight: bold; color: #0F5132; }
            .subtitle { font-size: 11px; color: #78350F; font-weight: bold; letter-spacing: 1px; }
            .receipt-no { position: absolute; top: 20px; right: 20px; font-size: 12px; font-weight: bold; color: #991B1B; font-family: monospace; }
            .amount-banner { background: #0F5132; color: #FEF3C7; font-size: 20px; font-weight: bold; text-align: center; padding: 10px; margin: 15px 0; border-radius: 4px; }
            .details { margin: 15px 0; font-size: 13px; }
            .row { display: flex; justify-content: space-between; border-bottom: 1px dashed #ccc; padding: 6px 0; }
            .label { font-weight: bold; color: #374151; }
            .value { font-weight: 600; color: #111827; }
            .signatures { margin-top: 35px; display: flex; justify-content: space-between; text-align: center; font-size: 11px; }
            .sig-box { width: 40%; border-top: 1px solid #000; padding-top: 4px; font-weight: bold; }
          </style>
        </head>
        <body>
          ${selectedTxs.map(tx => `
            <div class="receipt-page">
              <div class="receipt-no">NO: ${tx.voucherNo || 'REC-2026-OFFICIAL'}</div>
              <div class="header">
                <div class="title">UZAIRUE KINGDOM ROYAL CULTURAL FOUNDATION</div>
                <div class="subtitle">OFFICIAL TREASURY RECEIPT & ACKNOWLEDGMENT</div>
                <p style="font-size: 9px; margin: 2px 0;">Registered Non-Profit Cultural Institution & Organizers of ERUE Festival</p>
              </div>

              <div class="amount-banner">
                AMOUNT: ₦${tx.amount.toLocaleString()} (${tx.type.toUpperCase()})
              </div>

              <div class="details">
                <div class="row"><span class="label">Payer / Payee / Contributor:</span> <span class="value">${tx.payerOrPayee || 'Esteemed Patron'}</span></div>
                <div class="row"><span class="label">Date:</span> <span class="value">${tx.date}</span></div>
                <div class="row"><span class="label">Description:</span> <span class="value">${tx.description}</span></div>
                <div class="row"><span class="label">Category:</span> <span class="value">${tx.category}</span></div>
                <div class="row"><span class="label">Account Code:</span> <span class="value">${tx.accountCode}</span></div>
                <div class="row"><span class="label">Payment Channel:</span> <span class="value">${(tx.paymentMethod || 'Bank Wire').toUpperCase()}</span></div>
                <div class="row"><span class="label">Reference No:</span> <span class="value">${tx.referenceNo || 'N/A'}</span></div>
              </div>

              <div class="signatures">
                <div class="sig-box">
                  Recorded By: ${tx.createdBy || 'Financial Scribe'}<br/>
                  <span style="font-size: 9px; font-weight: normal; color: #555;">Treasury Officer</span>
                </div>
                <div class="sig-box">
                  Authorized: High Chief Aliu Momoh<br/>
                  <span style="font-size: 9px; font-weight: normal; color: #555;">Board of Trustees Lead</span>
                </div>
              </div>
            </div>
          `).join('')}
        </body>
      </html>
    `);
    printWin.document.close();
    printWin.print();
  };

  const handleSyncRaffle = () => {
    const syncedAmount = syncRaffleRevenueToFinancial();
    setStore(getFinancialStore());
    alert(`Successfully synced live ERUEWin Raffle Hub ticket sales!\nTotal Raffle Revenue: ₦${syncedAmount.toLocaleString()}`);
  };

  // Calculations for KPI
  const totalRevenues = store.transactions
    .filter(t => t.type !== 'expense' && t.status === 'cleared')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpenses = store.transactions
    .filter(t => t.type === 'expense' && t.status === 'cleared')
    .reduce((sum, t) => sum + t.amount, 0);

  const netSurplus = totalRevenues - totalExpenses;

  const totalPledged = store.pledges.reduce((sum, p) => sum + p.amountPledged, 0);
  const totalRedeemed = store.pledges.reduce((sum, p) => sum + p.amountRedeemed, 0);
  const outstandingPledges = totalPledged - totalRedeemed;

  const totalAssetsValue = store.assets.reduce((sum, a) => sum + a.value, 0) + netSurplus;

  // Handlers for New Transaction
  const handleCreateTransaction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTx.description || !newTx.amount || parseFloat(newTx.amount) <= 0) {
      alert('Please fill in a valid description and amount.');
      return;
    }

    const createdTx: FinancialTransaction = {
      id: `tx-${Date.now()}`,
      date: newTx.date,
      description: newTx.description,
      amount: parseFloat(newTx.amount),
      type: newTx.type,
      category: newTx.category,
      accountCode: newTx.accountCode,
      status: 'cleared',
      payerOrPayee: newTx.payerOrPayee || 'Uzairue Foundation Partner',
      paymentMethod: newTx.paymentMethod,
      referenceNo: newTx.referenceNo || `REF-${Math.floor(100000 + Math.random() * 900000)}`,
      voucherNo: newTx.type === 'expense' ? `PV-2026-${Math.floor(100 + Math.random() * 900)}` : `REC-2026-${Math.floor(100 + Math.random() * 900)}`,
      createdBy: currentUser?.name || 'Financial Scribe',
      approvedBy: 'Management Committee',
      notes: newTx.notes,
    };

    const updatedStore = {
      ...store,
      transactions: [createdTx, ...store.transactions],
    };
    saveFinancialStore(updatedStore, currentUser?.name || 'Financial Scribe');
    setStore(updatedStore);
    recordAuditLog({
      userName: currentUser?.name || 'Financial Scribe',
      userRole: currentUser?.role || 'writer',
      action: 'Recorded Financial Transaction / Ledger Entry',
      category: 'financial',
      details: `Recorded ${createdTx.type} of ₦${createdTx.amount.toLocaleString()} for ${createdTx.description} (Ref: ${createdTx.voucherNo})`,
    });
    setIsNewTxModalOpen(false);
    setNewTx({
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      type: 'revenue',
      category: 'Donations',
      accountCode: '40-100-001',
      payerOrPayee: '',
      paymentMethod: 'bank_transfer',
      referenceNo: '',
      notes: '',
    });
  };

  // Handlers for New Pledge
  const handleCreatePledge = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPledge.pledgerName || !newPledge.amountPledged || parseFloat(newPledge.amountPledged) <= 0) {
      alert('Please enter a valid pledger name and amount.');
      return;
    }

    const createdPledge: FestivalPledge = {
      id: `plg-${Date.now()}`,
      pledgerName: newPledge.pledgerName,
      titleOrOffice: newPledge.titleOrOffice || 'Esteemed Citizen',
      villageOrChapter: newPledge.villageOrChapter,
      pledgerType: newPledge.pledgerType,
      amountPledged: parseFloat(newPledge.amountPledged),
      amountRedeemed: 0,
      status: 'outstanding',
      pledgeDate: new Date().toISOString().split('T')[0],
      dueDate: newPledge.dueDate || '2026-08-30',
      purpose: newPledge.purpose || 'Festival Endowment & Cultural Support',
      contactEmail: newPledge.contactEmail,
      contactPhone: newPledge.contactPhone,
      redemptionHistory: [],
    };

    const updatedStore = {
      ...store,
      pledges: [createdPledge, ...store.pledges],
    };
    saveFinancialStore(updatedStore, currentUser?.name || 'Financial Scribe');
    setStore(updatedStore);
    setIsNewPledgeModalOpen(false);
    setNewPledge({
      pledgerName: '',
      titleOrOffice: '',
      villageOrChapter: 'Jattu-Uzairue',
      pledgerType: 'royal_chief',
      amountPledged: '',
      purpose: '',
      contactEmail: '',
      contactPhone: '',
      dueDate: '',
    });
  };

  // Handler for Pledge Retirement
  const handleRetirePledge = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPledgeToRetire) return;

    const amount = parseFloat(retireAmount);
    if (!amount || amount <= 0) {
      alert('Please enter a valid redemption amount.');
      return;
    }

    const updatedPledges = store.pledges.map(p => {
      if (p.id === selectedPledgeToRetire.id) {
        const newTotalRedeemed = p.amountRedeemed + amount;
        let newStatus: FestivalPledge['status'] = 'partially_redeemed';
        if (newTotalRedeemed >= p.amountPledged) {
          newStatus = 'fully_retired';
        }

        const redemptionEntry = {
          id: `red-${Date.now()}`,
          amount,
          date: new Date().toISOString().split('T')[0],
          paymentMethod: retireMethod,
          receiptNo: `REC-RET-${Math.floor(1000 + Math.random() * 9000)}`,
          recordedBy: currentUser?.name || 'Financial Scribe',
          bankRef: retireBankRef || `BANK-${Math.floor(100000 + Math.random() * 900000)}`,
        };

        return {
          ...p,
          amountRedeemed: newTotalRedeemed,
          status: newStatus,
          redemptionHistory: [redemptionEntry, ...p.redemptionHistory],
        };
      }
      return p;
    });

    // Also record a corresponding ledger transaction
    const pledgeTx: FinancialTransaction = {
      id: `tx-pledge-ret-${Date.now()}`,
      date: new Date().toISOString().split('T')[0],
      description: `Pledge Retirement: ${selectedPledgeToRetire.pledgerName} (${selectedPledgeToRetire.purpose})`,
      amount,
      type: 'pledge_redemption',
      category: 'Pledge Fulfillment',
      accountCode: '40-100-003',
      status: 'cleared',
      referenceNo: retireBankRef || `PLG-RET-${Date.now()}`,
      payerOrPayee: selectedPledgeToRetire.pledgerName,
      paymentMethod: 'bank_transfer',
      voucherNo: `REC-RET-${Math.floor(1000 + Math.random() * 9000)}`,
      createdBy: currentUser?.name || 'Financial Scribe',
      approvedBy: 'Management Committee',
      notes: `Pledge retirement recorded for ${selectedPledgeToRetire.villageOrChapter}`,
    };

    const updatedStore = {
      ...store,
      pledges: updatedPledges,
      transactions: [pledgeTx, ...store.transactions],
    };

    saveFinancialStore(updatedStore, currentUser?.name || 'Financial Scribe');
    setStore(updatedStore);
    setIsRetirePledgeModalOpen(false);
    setSelectedPledgeToRetire(null);
    setRetireAmount('');
    setRetireBankRef('');
    alert(`Pledge retirement of ₦${amount.toLocaleString()} recorded successfully!`);
  };

  // Handler for New Requisition
  const handleCreateRequisition = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newReq.title || !newReq.amount || parseFloat(newReq.amount) <= 0) {
      alert('Please enter a valid title and amount for requisition.');
      return;
    }

    const reqAmt = parseFloat(newReq.amount);
    const requiresBot = reqAmt > store.botApprovalThreshold;

    const createdReq: ExpenseRequisition = {
      id: `req-${Date.now()}`,
      requisitionNo: `REQ-2026-${Math.floor(100 + Math.random() * 900)}`,
      title: newReq.title,
      department: newReq.department,
      requestedBy: newReq.requestedBy,
      amount: reqAmt,
      description: newReq.description,
      accountCode: newReq.accountCode,
      status: 'submitted',
      priority: newReq.priority,
      requiresBotApproval: requiresBot,
      createdDate: new Date().toISOString().split('T')[0],
      beneficiaryName: newReq.beneficiaryName,
      bankAccountDetails: newReq.bankAccountDetails,
      approvalHistory: [
        {
          id: `app-${Date.now()}`,
          step: 'Submission',
          actorName: newReq.requestedBy,
          actorRole: 'Departmental Head / Lead',
          action: 'submitted',
          timestamp: new Date().toLocaleString(),
          notes: 'Requisition submitted for governance review.',
        },
      ],
    };

    const updatedStore = {
      ...store,
      requisitions: [createdReq, ...store.requisitions],
    };

    saveFinancialStore(updatedStore, currentUser?.name || 'Financial Scribe');
    setStore(updatedStore);
    setIsNewReqModalOpen(false);
    setNewReq({
      title: '',
      department: 'Logistics & Stage',
      requestedBy: currentUser?.name || 'Committee Scribe',
      amount: '',
      description: '',
      accountCode: '50-100-001',
      priority: 'normal',
      beneficiaryName: '',
      bankAccountDetails: '',
    });
    alert(`Requisition ${createdReq.requisitionNo} submitted successfully!`);
  };

  // Approval Workflow Actions
  const handleAdvanceRequisition = (
    req: ExpenseRequisition,
    action: 'mc_review' | 'bot_approve' | 'disburse' | 'reject'
  ) => {
    const actorName = currentUser?.name || (activeGovernanceRole === 'bot_member' ? 'High Chief Aliu Momoh (BoT)' : 'Engr. Abdulazeez Haruna (MC)');
    const actorRole = activeGovernanceRole === 'bot_member' ? 'Board of Trustees Member' : 'Management Committee Executive';

    let nextStatus: ExpenseRequisition['status'] = req.status;
    let actionLabel = '';
    let pvNo = req.paymentVoucherNo;

    if (action === 'mc_review') {
      nextStatus = 'mc_reviewed';
      actionLabel = 'reviewed and passed for approval';
    } else if (action === 'bot_approve') {
      nextStatus = 'bot_approved';
      actionLabel = 'authorized by Board of Trustees';
    } else if (action === 'disburse') {
      nextStatus = 'disbursed';
      actionLabel = 'disbursed & Payment Voucher issued';
      pvNo = req.paymentVoucherNo || `PV-2026-${Math.floor(1000 + Math.random() * 9000)}`;
    } else if (action === 'reject') {
      nextStatus = 'rejected';
      actionLabel = 'rejected during governance review';
    }

    const updatedReqs = store.requisitions.map(r => {
      if (r.id === req.id) {
        const stepName =
          action === 'mc_review'
            ? 'Management Review'
            : action === 'bot_approve'
            ? 'Board of Trustees Approval'
            : action === 'disburse'
            ? 'Disbursement'
            : 'Governance Rejection';

        const historyEntry = {
          id: `app-${Date.now()}`,
          step: stepName as any,
          actorName,
          actorRole,
          action: action === 'disburse' ? ('disbursed' as const) : action === 'reject' ? ('rejected' as const) : ('approved' as const),
          timestamp: new Date().toLocaleString(),
          notes: `Action executed under ${actorRole} mandate.`,
        };

        return {
          ...r,
          status: nextStatus,
          paymentVoucherNo: pvNo,
          disbursedDate: action === 'disburse' ? new Date().toISOString().split('T')[0] : r.disbursedDate,
          approvalHistory: [...r.approvalHistory, historyEntry],
        };
      }
      return r;
    });

    let updatedTxList = [...store.transactions];

    // If disbursed, create a cleared expense transaction automatically
    if (action === 'disburse') {
      const expTx: FinancialTransaction = {
        id: `tx-disb-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        description: `Disbursement: ${req.title} (${req.requisitionNo})`,
        amount: req.amount,
        type: 'expense',
        category: req.department,
        accountCode: req.accountCode,
        status: 'cleared',
        referenceNo: req.requisitionNo,
        payerOrPayee: req.beneficiaryName || req.requestedBy,
        paymentMethod: 'bank_transfer',
        voucherNo: pvNo,
        createdBy: actorName,
        approvedBy: 'Board of Trustees & Management Committee',
        notes: `Disbursed for ${req.description}`,
      };
      updatedTxList.unshift(expTx);
    }

    const updatedStore = {
      ...store,
      requisitions: updatedReqs,
      transactions: updatedTxList,
    };

    saveFinancialStore(updatedStore, actorName);
    setStore(updatedStore);
    if (selectedReqForDetail && selectedReqForDetail.id === req.id) {
      setSelectedReqForDetail(updatedReqs.find(item => item.id === req.id) || null);
    }
    alert(`Requisition ${req.requisitionNo} successfully ${actionLabel}!`);
  };

  // PRINT ENGINES
  const handlePrintPaymentVoucher = (req: ExpenseRequisition) => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>PAYMENT VOUCHER - ${req.requisitionNo}</title>
          <style>
            body { font-family: 'Times New Roman', serif; margin: 30px; color: #111; line-height: 1.5; }
            .header { text-align: center; border-bottom: 3px double #0F5132; padding-bottom: 15px; margin-bottom: 20px; }
            .title { font-size: 20px; font-weight: bold; color: #0F5132; text-transform: uppercase; letter-spacing: 1px; }
            .subtitle { font-size: 12px; font-weight: bold; color: #78350F; }
            .voucher-title { font-size: 18px; font-weight: bold; margin-top: 15px; background: #0F5132; color: #fff; padding: 6px; text-align: center; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-top: 20px; font-size: 13px; }
            .box { border: 1px solid #ccc; padding: 10px; background: #fafafa; }
            .amount-box { font-size: 22px; font-weight: bold; color: #0F5132; border: 2px solid #0F5132; padding: 10px; text-align: center; margin: 20px 0; background: #f0fdf4; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; }
            th, td { border: 1px solid #999; padding: 8px; text-align: left; }
            th { background: #e2e8f0; }
            .signatures { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; text-align: center; font-size: 11px; }
            .sig-line { border-top: 1px solid #000; margin-top: 45px; padding-top: 5px; font-weight: bold; }
            .watermark { position: absolute; top: 35%; left: 15%; font-size: 60px; color: rgba(15, 81, 50, 0.06); transform: rotate(-30deg); font-weight: bold; pointer-events: none; }
          </style>
        </head>
        <body>
          <div class="watermark">UZAIRUE ROYAL FOUNDATION</div>
          <div class="header">
            <div class="title">UZAIRUE KINGDOM FESTIVAL CENTRAL COMMITTEE</div>
            <div class="subtitle">& ROYAL CULTURAL FOUNDATION INSTITUTION</div>
            <p style="font-size: 11px; margin: 4px 0;">Palace Seat, Jattu-Uzairue, Etsako West LGA, Edo State, Nigeria</p>
          </div>

          <div class="voucher-title">OFFICIAL DISBURSEMENT PAYMENT VOUCHER (PV)</div>

          <div class="grid">
            <div class="box">
              <strong>Voucher Serial No:</strong> ${req.paymentVoucherNo || 'PV-2026-PENDING'}<br/>
              <strong>Requisition Reference:</strong> ${req.requisitionNo}<br/>
              <strong>Date of Disbursement:</strong> ${req.disbursedDate || new Date().toLocaleDateString()}<br/>
              <strong>Account Head:</strong> ${req.accountCode} - ${CHART_OF_ACCOUNTS.find(a => a.code === req.accountCode)?.name || 'General Expense'}
            </div>
            <div class="box">
              <strong>Department:</strong> ${req.department}<br/>
              <strong>Beneficiary / Payee:</strong> ${req.beneficiaryName || req.requestedBy}<br/>
              <strong>Bank Account Details:</strong> ${req.bankAccountDetails || 'N/A (Direct Cash Transfer)'}<br/>
              <strong>Priority Level:</strong> ${req.priority.toUpperCase()}
            </div>
          </div>

          <div class="amount-box">
            AMOUNT PAID: ₦${req.amount.toLocaleString()}
          </div>

          <table>
            <thead>
              <tr>
                <th>Description of Expenditure & Justification</th>
                <th>Requested By</th>
                <th>Governance Threshold</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>${req.title}</strong><br/>${req.description}</td>
                <td>${req.requestedBy}</td>
                <td>${req.requiresBotApproval ? 'Board of Trustees (High Tier > ₦500k)' : 'Management Committee Tier'}</td>
              </tr>
            </tbody>
          </table>

          <h4 style="margin-top: 25px; margin-bottom: 5px; font-size: 12px;">Governance Authorization & Approval Trail:</h4>
          <table>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Authorizing Officer</th>
                <th>Role</th>
                <th>Action</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              ${req.approvalHistory.map(h => `
                <tr>
                  <td>${h.step}</td>
                  <td>${h.actorName}</td>
                  <td>${h.actorRole}</td>
                  <td>${h.action.toUpperCase()}</td>
                  <td>${h.timestamp}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="signatures">
            <div>
              <div class="sig-line">Prepared By (Financial Scribe)</div>
              Ikpemhi Oshioke
            </div>
            <div>
              <div class="sig-line">Verified By (MC Chairman)</div>
              Engr. Abdulazeez Haruna
            </div>
            <div>
              <div class="sig-line">Authorized By (BoT Lead / Royal Seal)</div>
              High Chief Aliu Momoh
            </div>
          </div>

          <div style="margin-top: 30px; font-size: 9px; text-align: center; color: #666; border-top: 1px solid #ddd; padding-top: 5px;">
            This is an official financial accounting record of the Uzairue Festival Central Committee. Valid only when signed by authorized Board Trustees.
          </div>
        </body>
      </html>
    `);
    printWin.document.close();
    printWin.print();
  };

  const handlePrintReceipt = (tx: FinancialTransaction) => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>RECEIPT - ${tx.voucherNo || tx.referenceNo}</title>
          <style>
            body { font-family: 'Georgia', serif; margin: 40px; color: #111; line-height: 1.6; }
            .border-wrap { border: 8px double #0F5132; padding: 25px; background: #fffdfa; position: relative; }
            .header { text-align: center; border-bottom: 2px solid #78350F; padding-bottom: 12px; margin-bottom: 20px; }
            .title { font-size: 22px; font-weight: bold; color: #0F5132; }
            .subtitle { font-size: 12px; color: #78350F; font-weight: bold; letter-spacing: 1px; }
            .receipt-no { position: absolute; top: 25px; right: 25px; font-size: 13px; font-weight: bold; color: #991B1B; font-family: monospace; }
            .amount-banner { background: #0F5132; color: #FEF3C7; font-size: 24px; font-weight: bold; text-align: center; padding: 12px; margin: 20px 0; border-radius: 4px; }
            .details { margin: 20px 0; font-size: 14px; }
            .row { display: flex; justify-content: space-between; border-bottom: 1px dashed #ccc; padding: 8px 0; }
            .label { font-weight: bold; color: #374151; }
            .value { font-weight: 600; color: #111827; }
            .signatures { margin-top: 50px; display: flex; justify-content: space-between; text-align: center; font-size: 12px; }
            .sig-box { width: 40%; border-top: 1px solid #000; padding-top: 5px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="border-wrap">
            <div class="receipt-no">NO: ${tx.voucherNo || 'REC-2026-OFFICIAL'}</div>
            <div class="header">
              <div class="title">UZAIRUE KINGDOM ROYAL CULTURAL FOUNDATION</div>
              <div class="subtitle">OFFICIAL TREASURY RECEIPT & ACKNOWLEDGMENT</div>
              <p style="font-size: 10px; margin: 2px 0;">Registered Non-Profit Cultural Institution & Organizers of ERUE Festival</p>
            </div>

            <div class="amount-banner">
              RECEIVED: ₦${tx.amount.toLocaleString()}
            </div>

            <div class="details">
              <div class="row"><span class="label">Received From / Contributor:</span> <span class="value">${tx.payerOrPayee || 'Esteemed Donor / Patron'}</span></div>
              <div class="row"><span class="label">Date of Receipt:</span> <span class="value">${tx.date}</span></div>
              <div class="row"><span class="label">Payment Purpose / Description:</span> <span class="value">${tx.description}</span></div>
              <div class="row"><span class="label">Category & Classification:</span> <span class="value">${tx.category}</span></div>
              <div class="row"><span class="label">Account Allocation Code:</span> <span class="value">${tx.accountCode} - ${CHART_OF_ACCOUNTS.find(a => a.code === tx.accountCode)?.name}</span></div>
              <div class="row"><span class="label">Payment Gateway / Channel:</span> <span class="value">${(tx.paymentMethod || 'Bank Wire').toUpperCase()}</span></div>
              <div class="row"><span class="label">Bank Reference / Audit Ref:</span> <span class="value">${tx.referenceNo || 'N/A'}</span></div>
            </div>

            <div class="signatures">
              <div class="sig-box">
                Recorded By: ${tx.createdBy || 'Financial Scribe'}<br/>
                <span style="font-size: 10px; font-weight: normal; color: #555;">Treasury Officer</span>
              </div>
              <div class="sig-box">
                Authorized Signature: High Chief Aliu Momoh<br/>
                <span style="font-size: 10px; font-weight: normal; color: #555;">Board of Trustees Lead / Royal Seal</span>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWin.document.close();
    printWin.print();
  };

  const handlePrintAuditReport = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>OFFICIAL FINANCIAL DISCLOSURE & AUDIT CERTIFICATE - ERUE FESTIVAL 2026</title>
          <style>
            body { font-family: 'Times New Roman', serif; margin: 35px; color: #111; line-height: 1.5; }
            .header { text-align: center; border-bottom: 3px double #0F5132; padding-bottom: 15px; margin-bottom: 25px; }
            .title { font-size: 22px; font-weight: bold; color: #0F5132; }
            .subtitle { font-size: 13px; font-weight: bold; color: #78350F; }
            .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 25px; text-align: center; }
            .kpi-card { border: 1px solid #0F5132; padding: 10px; background: #f0fdf4; }
            .kpi-val { font-size: 16px; font-weight: bold; color: #0F5132; }
            .kpi-lbl { font-size: 10px; text-transform: uppercase; color: #4b5563; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; }
            th { background: #0F5132; color: #fff; text-transform: uppercase; font-size: 10px; }
            .section-head { font-size: 14px; font-weight: bold; color: #0F5132; margin-top: 25px; border-bottom: 1px solid #0F5132; padding-bottom: 3px; }
            .signatures { margin-top: 45px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; text-align: center; font-size: 11px; }
            .sig-line { border-top: 1px solid #000; margin-top: 45px; padding-top: 5px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">UZAIRUE KINGDOM FESTIVAL CENTRAL STEERING COMMITTEE</div>
            <div class="subtitle">ANNUAL FINANCIAL DISCLOSURE STATEMENT & INDEPENDENT AUDIT CERTIFICATE</div>
            <p style="font-size: 11px; margin-top: 5px;">Published for the Uzairue Royal Traditional Council, Board of Trustees, and Uzairue Diaspora Worldwide</p>
          </div>

          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-val">₦${totalRevenues.toLocaleString()}</div>
              <div class="kpi-lbl">Total Gross Revenue</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-val">₦${totalExpenses.toLocaleString()}</div>
              <div class="kpi-lbl">Total Operating Expense</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-val">₦${netSurplus.toLocaleString()}</div>
              <div class="kpi-lbl">Net Festival Surplus</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-val">₦${totalAssetsValue.toLocaleString()}</div>
              <div class="kpi-lbl">Total Asset Value</div>
            </div>
          </div>

          <div class="section-head">1. STATEMENT OF INCOME & EXPENDITURE (NPO SUMMARY)</div>
          <table>
            <thead>
              <tr>
                <th>Account Code</th>
                <th>Revenue / Income Source Description</th>
                <th>Classification</th>
                <th>Amount (₦)</th>
              </tr>
            </thead>
            <tbody>
              ${store.transactions.filter(t => t.type !== 'expense').map(t => `
                <tr>
                  <td>${t.accountCode}</td>
                  <td>${t.description}</td>
                  <td>${t.category}</td>
                  <td>₦${t.amount.toLocaleString()}</td>
                </tr>
              `).join('')}
              <tr style="font-weight: bold; background: #e2e8f0;">
                <td colspan="3">TOTAL REVENUE & PHILANTHROPIC INFLOWS</td>
                <td>₦${totalRevenues.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>

          <table style="margin-top: 15px;">
            <thead>
              <tr>
                <th>Account Code</th>
                <th>Expenditure Item & Justification</th>
                <th>Department</th>
                <th>Amount (₦)</th>
              </tr>
            </thead>
            <tbody>
              ${store.transactions.filter(t => t.type === 'expense').map(t => `
                <tr>
                  <td>${t.accountCode}</td>
                  <td>${t.description}</td>
                  <td>${t.category}</td>
                  <td>₦${t.amount.toLocaleString()}</td>
                </tr>
              `).join('')}
              <tr style="font-weight: bold; background: #fee2e2;">
                <td colspan="3">TOTAL OPERATING EXPENSES</td>
                <td>₦${totalExpenses.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>

          <div class="section-head">2. OUTSTANDING PLEDGES & REDEMPTION STATUS</div>
          <table>
            <thead>
              <tr>
                <th>Pledger Name</th>
                <th>Village / Chapter</th>
                <th>Pledged (₦)</th>
                <th>Redeemed (₦)</th>
                <th>Outstanding (₦)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${store.pledges.map(p => `
                <tr>
                  <td>${p.pledgerName}</td>
                  <td>${p.villageOrChapter}</td>
                  <td>₦${p.amountPledged.toLocaleString()}</td>
                  <td>₦${p.amountRedeemed.toLocaleString()}</td>
                  <td>₦${(p.amountPledged - p.amountRedeemed).toLocaleString()}</td>
                  <td>${p.status.toUpperCase()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="signatures">
            <div>
              <div class="sig-line">Prepared By (Financial Scribe)</div>
              Ikpemhi Oshioke
            </div>
            <div>
              <div class="sig-line">Verified By (MC Chairman)</div>
              Engr. Abdulazeez Haruna
            </div>
            <div>
              <div class="sig-line">Audited & Certified (BoT Treasurer)</div>
              High Chief Aliu Momoh
            </div>
          </div>
        </body>
      </html>
    `);
    printWin.document.close();
    printWin.print();
  };

  // Filtered Lists
  const filteredTransactions = store.transactions.filter(t => {
    const matchesSearch =
      t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.payerOrPayee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.accountCode.includes(searchTerm);
    const matchesType = typeFilter === 'all' || t.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const filteredPledges = store.pledges.filter(p => {
    const matchesSearch =
      p.pledgerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.villageOrChapter.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.purpose.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = pledgeFilter === 'all' || p.status === pledgeFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredRequisitions = store.requisitions.filter(r => {
    const matchesSearch =
      r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.requisitionNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.requestedBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = reqStatusFilter === 'all' || r.status === reqStatusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* HEADER BANNER */}
      <div className="bg-gradient-to-r from-[#0F5132] via-[#07301d] to-[#041a10] text-white p-6 sm:p-8 rounded-3xl shadow-xl border border-amber-500/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl pointer-events-none" />
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold border border-amber-400/30 mb-2">
              <span>🏛️ Institutional Non-Profit Governance Engine</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-amber-200 font-serif tracking-tight">
              Uzairue Festival Financial Management & Governance Suite
            </h1>
            <p className="text-xs sm:text-sm text-emerald-100/80 max-w-2xl mt-1">
              2-Tier Governance Architecture (Board of Trustees & Management Committee) | CAMA 2020 NPO Standards | Real-Time ERUEWin Raffle Sync & Public Disclosures
            </p>
          </div>

          {/* Quick Stats & Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSyncRaffle}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-4 py-2.5 rounded-xl text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer"
            >
              <span>🎟️</span> Sync ERUEWin Raffle Sales
            </button>

            <button
              onClick={handlePrintAuditReport}
              className="bg-emerald-800/80 hover:bg-emerald-700 text-amber-200 border border-amber-400/40 font-bold px-4 py-2.5 rounded-xl text-xs transition-all flex items-center gap-2 cursor-pointer"
            >
              <span>🖨️</span> Print Financial Audit Certificate
            </button>

            {/* Governance Role Perspective Selector */}
            <div className="bg-black/30 backdrop-blur-md p-1.5 rounded-xl border border-white/10 flex items-center gap-1">
              <span className="text-[10px] text-amber-300/80 px-2 font-bold uppercase">Role:</span>
              <button
                onClick={() => setActiveGovernanceRole('mc_executive')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  activeGovernanceRole === 'mc_executive'
                    ? 'bg-amber-400 text-slate-950 shadow-sm'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Management Comm. (MC)
              </button>
              <button
                onClick={() => setActiveGovernanceRole('bot_member')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  activeGovernanceRole === 'bot_member'
                    ? 'bg-amber-400 text-slate-950 shadow-sm'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Board of Trustees (BoT)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* TOP METRICS SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Gross Revenue</p>
          <p className="text-lg font-extrabold text-[#0F5132] font-mono mt-1">₦{totalRevenues.toLocaleString()}</p>
          <p className="text-[10px] text-emerald-600 mt-1 font-semibold">Donations, Store & Raffle</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Total Expenses</p>
          <p className="text-lg font-extrabold text-red-700 font-mono mt-1">₦{totalExpenses.toLocaleString()}</p>
          <p className="text-[10px] text-red-600 mt-1 font-semibold">Stage, Troupes & Security</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Net Festival Surplus</p>
          <p className="text-lg font-extrabold text-amber-700 font-mono mt-1">₦{netSurplus.toLocaleString()}</p>
          <p className="text-[10px] text-amber-600 mt-1 font-semibold">Unexpended Reserves</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Total Pledged</p>
          <p className="text-lg font-extrabold text-indigo-900 font-mono mt-1">₦{totalPledged.toLocaleString()}</p>
          <p className="text-[10px] text-indigo-600 mt-1 font-semibold">Chiefs & Diaspora Union</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Outstanding Pledges</p>
          <p className="text-lg font-extrabold text-amber-800 font-mono mt-1">₦{outstandingPledges.toLocaleString()}</p>
          <p className="text-[10px] text-amber-700 mt-1 font-semibold">Awaiting Retirement</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
          <p className="text-[10px] font-bold text-gray-500 uppercase">Total Asset Value</p>
          <p className="text-lg font-extrabold text-teal-800 font-mono mt-1">₦{totalAssetsValue.toLocaleString()}</p>
          <p className="text-[10px] text-teal-600 mt-1 font-semibold">Equipment & Star Prizes</p>
        </div>
      </div>

      {/* NAVIGATION TABS */}
      <div className="bg-white p-2 rounded-2xl border border-gray-200 shadow-sm flex flex-wrap gap-2">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'dashboard'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>📊</span> Executive Dashboard
        </button>

        <button
          onClick={() => setActiveTab('transactions')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'transactions'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>📒</span> General Ledger ({store.transactions.length})
        </button>

        <button
          onClick={() => setActiveTab('pledges')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'pledges'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>🤝</span> Pledges Bureau ({store.pledges.length})
        </button>

        <button
          onClick={() => setActiveTab('requisitions')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'requisitions'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>🏛️</span> 2-Tier Approvals ({store.requisitions.length})
        </button>

        <button
          onClick={() => setActiveTab('budgets')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'budgets'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>📉</span> Budget vs Actual
        </button>

        <button
          onClick={() => setActiveTab('statements')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'statements'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>📑</span> Audit Disclosures
        </button>

        <button
          onClick={() => setActiveTab('governance')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'governance'
              ? 'bg-[#0F5132] text-amber-300 shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <span>⚖️</span> Governance Framework
        </button>
      </div>

      {/* SEARCH & QUICK ACTION BAR (when applicable) */}
      {(activeTab === 'transactions' || activeTab === 'pledges' || activeTab === 'requisitions') && (
        <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search descriptions, names, refs, account codes..."
              className="w-full pl-9 pr-4 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0F5132] focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto">
            {activeTab === 'transactions' && (
              <>
                <select
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white font-medium focus:ring-2 focus:ring-[#0F5132]"
                >
                  <option value="all">All Types</option>
                  <option value="revenue">Revenues</option>
                  <option value="expense">Expenses</option>
                  <option value="donation">Donations</option>
                  <option value="pledge_redemption">Pledge Redemptions</option>
                  <option value="store_sale">Store Sales</option>
                  <option value="raffle_sale">Raffle Sales</option>
                </select>
                <button
                  onClick={() => setIsNewTxModalOpen(true)}
                  className="px-4 py-2 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                >
                  <span>➕</span> Record New Entry
                </button>
              </>
            )}

            {activeTab === 'pledges' && (
              <>
                <select
                  value={pledgeFilter}
                  onChange={e => setPledgeFilter(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white font-medium focus:ring-2 focus:ring-[#0F5132]"
                >
                  <option value="all">All Pledge Statuses</option>
                  <option value="outstanding">Outstanding</option>
                  <option value="partially_redeemed">Partially Redeemed</option>
                  <option value="fully_retired">Fully Retired</option>
                </select>
                <button
                  onClick={() => setIsNewPledgeModalOpen(true)}
                  className="px-4 py-2 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                >
                  <span>✍️</span> Document New Pledge
                </button>
              </>
            )}

            {activeTab === 'requisitions' && (
              <>
                <select
                  value={reqStatusFilter}
                  onChange={e => setReqStatusFilter(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white font-medium focus:ring-2 focus:ring-[#0F5132]"
                >
                  <option value="all">All Statuses</option>
                  <option value="submitted">Submitted (Pending MC)</option>
                  <option value="mc_reviewed">MC Reviewed (Pending BoT)</option>
                  <option value="bot_approved">BoT Approved</option>
                  <option value="disbursed">Disbursed</option>
                </select>
                <button
                  onClick={() => setIsNewReqModalOpen(true)}
                  className="px-4 py-2 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                >
                  <span>📝</span> Submit New Requisition
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* TAB CONTENT: DASHBOARD */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Action Hub */}
            <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-[#0F5132] font-serif border-b border-gray-100 pb-2">
                ⚡ Rapid Treasury Actions
              </h3>
              <div className="grid grid-cols-1 gap-2.5">
                <button
                  onClick={() => setIsNewTxModalOpen(true)}
                  className="p-3 bg-emerald-50 hover:bg-emerald-100 text-[#0F5132] rounded-2xl text-xs font-bold text-left transition-all border border-emerald-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">💰</span>
                    <div>
                      <p className="font-extrabold">Record Direct Revenue or Cash Deposit</p>
                      <p className="text-[10px] text-gray-500 font-normal">Add philanthropic donation, booth fee, or offline store sale</p>
                    </div>
                  </div>
                  <span>➔</span>
                </button>

                <button
                  onClick={() => setIsNewReqModalOpen(true)}
                  className="p-3 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-2xl text-xs font-bold text-left transition-all border border-amber-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">📝</span>
                    <div>
                      <p className="font-extrabold">Submit Expenditure Requisition</p>
                      <p className="text-[10px] text-amber-800/80 font-normal">Requisition funds for logistics, troupes, security, or publicity</p>
                    </div>
                  </div>
                  <span>➔</span>
                </button>

                <button
                  onClick={() => setIsNewPledgeModalOpen(true)}
                  className="p-3 bg-blue-50 hover:bg-blue-100 text-blue-900 rounded-2xl text-xs font-bold text-left transition-all border border-blue-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">🤝</span>
                    <div>
                      <p className="font-extrabold">Register Royal / Diaspora Pledge</p>
                      <p className="text-[10px] text-blue-800/80 font-normal">Record formal commitments from chiefs, chapters, or sponsors</p>
                    </div>
                  </div>
                  <span>➔</span>
                </button>

                <button
                  onClick={handleSyncRaffle}
                  className="p-3 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-2xl text-xs font-bold text-left transition-all border border-purple-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">🎟️</span>
                    <div>
                      <p className="font-extrabold">Sync Live ERUEWin Raffle Income</p>
                      <p className="text-[10px] text-purple-800/80 font-normal">Pull real-time ticket sales into general ledger account 40-100-002</p>
                    </div>
                  </div>
                  <span>➔</span>
                </button>
              </div>
            </div>

            {/* Department Budget Health Overview */}
            <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4 lg:col-span-2">
              <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                <h3 className="text-base font-bold text-[#0F5132] font-serif">
                  📊 Departmental Expenditure & Budget Allocation
                </h3>
                <span className="text-xs text-gray-500 font-semibold">CAMA 2020 Compliance</span>
              </div>

              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {store.budgets.map(b => {
                  const pct = Math.min(100, Math.round(((b.spentAmount + b.committedAmount) / b.allocatedAmount) * 100));
                  return (
                    <div key={b.code} className="space-y-1 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-gray-800">{b.departmentName}</span>
                        <span className="font-mono text-gray-600">
                          ₦{(b.spentAmount + b.committedAmount).toLocaleString()} / ₦{b.allocatedAmount.toLocaleString()} ({pct}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all rounded-full ${
                            pct > 90 ? 'bg-red-600' : pct > 60 ? 'bg-amber-500' : 'bg-emerald-600'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent General Ledger Activity Table */}
          <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-bold text-[#0F5132] font-serif">
                🕒 Recent Ledger Transactions
              </h3>
              <button
                onClick={() => setActiveTab('transactions')}
                className="text-xs font-bold text-[#0F5132] hover:underline"
              >
                View All Ledger Entries ➔
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-100 text-gray-600 font-bold uppercase">
                  <tr>
                    <th className="p-3">Date</th>
                    <th className="p-3">Description</th>
                    <th className="p-3">Category / Account</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Payer/Payee</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {store.transactions.slice(0, 5).map(t => (
                    <tr key={t.id} className="hover:bg-gray-50/80">
                      <td className="p-3 font-mono text-gray-500">{t.date}</td>
                      <td className="p-3 font-semibold text-gray-900">{t.description}</td>
                      <td className="p-3 text-gray-600">
                        <span className="font-mono text-[10px] bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 font-bold">
                          {t.accountCode}
                        </span>{' '}
                        {t.category}
                      </td>
                      <td className={`p-3 font-mono font-bold ${t.type === 'expense' ? 'text-red-700' : 'text-emerald-800'}`}>
                        {t.type === 'expense' ? '-' : '+'}₦{t.amount.toLocaleString()}
                      </td>
                      <td className="p-3 text-gray-600">{t.payerOrPayee || 'N/A'}</td>
                      <td className="p-3">
                        <button
                          onClick={() => handlePrintReceipt(t)}
                          className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-[#0F5132] font-bold rounded-lg text-[10px] border border-gray-300 flex items-center gap-1 cursor-pointer"
                        >
                          <span>🖨️</span> Receipt
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: GENERAL LEDGER TRANSACTIONS */}
      {activeTab === 'transactions' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-base font-bold text-[#0F5132] font-serif">General Ledger & Double-Entry Accounting Log</h3>
              <p className="text-xs text-gray-500">Showing {filteredTransactions.length} transaction entries ({selectedTransactionIds.length} selected)</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkPrintReceipts}
                disabled={selectedTransactionIds.length === 0}
                className={`px-4 py-2 rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-1.5 ${
                  selectedTransactionIds.length > 0
                    ? 'bg-amber-500 hover:bg-amber-600 text-gray-950 cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <span>🖨️</span> Generate Receipts for Selected ({selectedTransactionIds.length})
              </button>
              <button
                onClick={() => setIsNewTxModalOpen(true)}
                className="px-4 py-2 bg-[#0F5132] text-amber-300 rounded-xl text-xs font-bold shadow-md cursor-pointer"
              >
                ➕ Record Entry
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-[#0F5132] text-amber-300 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={filteredTransactions.length > 0 && selectedTransactionIds.length === filteredTransactions.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTransactionIds(filteredTransactions.map(t => t.id));
                        } else {
                          setSelectedTransactionIds([]);
                        }
                      }}
                      className="rounded text-[#0F5132] focus:ring-amber-300 cursor-pointer"
                    />
                  </th>
                  <th className="p-3">Ref / Voucher</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Account Code</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Channel / Method</th>
                  <th className="p-3">Recorded By</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTransactions.map(t => {
                  const account = CHART_OF_ACCOUNTS.find(a => a.code === t.accountCode);
                  const isSelected = selectedTransactionIds.includes(t.id);
                  return (
                    <tr key={t.id} className={`hover:bg-amber-50/40 ${isSelected ? 'bg-amber-50/60' : ''}`}>
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTransactionIds([...selectedTransactionIds, t.id]);
                            } else {
                              setSelectedTransactionIds(selectedTransactionIds.filter(id => id !== t.id));
                            }
                          }}
                          className="rounded text-[#0F5132] focus:ring-amber-300 cursor-pointer"
                        />
                      </td>
                      <td className="p-3 font-mono font-bold text-gray-700">{t.voucherNo || t.referenceNo}</td>
                      <td className="p-3 font-mono text-gray-500">{t.date}</td>
                      <td className="p-3 font-semibold text-gray-900">{t.description}</td>
                      <td className="p-3 font-mono">
                        <span className="bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 text-gray-700 font-bold">
                          {t.accountCode}
                        </span>
                        <div className="text-[10px] text-gray-500 font-sans truncate max-w-xs">{account?.name}</div>
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                            t.type === 'expense'
                              ? 'bg-red-100 text-red-800'
                              : t.type === 'donation'
                              ? 'bg-emerald-100 text-emerald-800'
                              : t.type === 'pledge_redemption'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {t.type}
                        </span>
                      </td>
                      <td className={`p-3 font-mono font-extrabold text-sm ${t.type === 'expense' ? 'text-red-700' : 'text-emerald-800'}`}>
                        {t.type === 'expense' ? '-' : '+'}₦{t.amount.toLocaleString()}
                      </td>
                      <td className="p-3 text-gray-600 capitalize">{t.paymentMethod?.replace('_', ' ') || 'Bank Transfer'}</td>
                      <td className="p-3 text-gray-500">{t.createdBy || 'Scribe'}</td>
                      <td className="p-3">
                        <button
                          onClick={() => handlePrintReceipt(t)}
                          className="px-2.5 py-1 bg-white hover:bg-gray-100 text-[#0F5132] border border-[#0F5132]/30 rounded-lg text-[10px] font-bold cursor-pointer transition-all"
                        >
                          🖨️ Receipt
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: PLEDGES BUREAU */}
      {activeTab === 'pledges' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-base font-bold text-[#0F5132] font-serif">Pledges & Philanthropic Commitments Bureau</h3>
              <p className="text-xs text-gray-500">Track and retire commitments made by Royal Chiefs, Diaspora Chapters, and Corporate Sponsors</p>
            </div>
            <button
              onClick={() => setIsNewPledgeModalOpen(true)}
              className="px-4 py-2 bg-[#0F5132] text-amber-300 rounded-xl text-xs font-bold shadow-md cursor-pointer"
            >
              ✍️ Register New Pledge
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPledges.map(p => {
              const outstanding = p.amountPledged - p.amountRedeemed;
              const pct = Math.round((p.amountRedeemed / p.amountPledged) * 100);

              return (
                <div key={p.id} className="bg-gray-50 p-5 rounded-2xl border border-gray-200 space-y-3 hover:border-amber-400 transition-all">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-[10px] font-bold bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full uppercase">
                        {p.villageOrChapter} &bull; {p.pledgerType.replace('_', ' ')}
                      </span>
                      <h4 className="text-sm font-extrabold text-gray-900 mt-1">{p.pledgerName}</h4>
                      <p className="text-xs text-gray-500">{p.titleOrOffice}</p>
                    </div>

                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                        p.status === 'fully_retired'
                          ? 'bg-emerald-100 text-emerald-800'
                          : p.status === 'partially_redeemed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {p.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="p-3 bg-white rounded-xl border border-gray-100 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount Pledged:</span>
                      <span className="font-mono font-bold text-gray-900">₦{p.amountPledged.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount Redeemed:</span>
                      <span className="font-mono font-bold text-emerald-700">₦{p.amountRedeemed.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-1">
                      <span className="text-gray-700 font-bold">Outstanding Balance:</span>
                      <span className="font-mono font-bold text-amber-800">₦{outstanding.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Progress Meter */}
                  <div>
                    <div className="flex justify-between text-[10px] font-bold text-gray-500 mb-1">
                      <span>Fulfillment Ratio</span>
                      <span>{pct}% Retired</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                      <div className="bg-emerald-600 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 italic">Target Purpose: {p.purpose}</p>

                  <div className="pt-2 flex justify-between items-center border-t border-gray-200">
                    <span className="text-[10px] text-gray-400 font-mono">Pledged: {p.pledgeDate}</span>
                    {p.status !== 'fully_retired' && (
                      <button
                        onClick={() => {
                          setSelectedPledgeToRetire(p);
                          setRetireAmount(outstanding.toString());
                          setIsRetirePledgeModalOpen(true);
                        }}
                        className="px-3 py-1.5 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 rounded-lg text-xs font-bold cursor-pointer shadow-sm transition-all"
                      >
                        💵 Fulfill / Retire Pledge
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB CONTENT: 2-TIER REQUISITION APPROVAL WORKFLOW */}
      {activeTab === 'requisitions' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-base font-bold text-[#0F5132] font-serif">2-Tier Governance Expense Requisitions & Approvals</h3>
              <p className="text-xs text-gray-500">
                Management Committee Initial Review ➔ Board of Trustees (BoT) Final Authorization for spend &gt; ₦500,000
              </p>
            </div>
            <button
              onClick={() => setIsNewReqModalOpen(true)}
              className="px-4 py-2 bg-[#0F5132] text-amber-300 rounded-xl text-xs font-bold shadow-md cursor-pointer"
            >
              📝 New Requisition
            </button>
          </div>

          <div className="space-y-3">
            {filteredRequisitions.map(r => {
              const isHighTier = r.amount > store.botApprovalThreshold;

              return (
                <div key={r.id} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-3 hover:border-amber-400 transition-all">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold bg-[#0F5132] text-amber-300 px-2 py-0.5 rounded">
                          {r.requisitionNo}
                        </span>
                        <span className="text-xs font-bold text-gray-700">{r.department}</span>
                        {isHighTier && (
                          <span className="text-[10px] font-black bg-purple-100 text-purple-900 px-2 py-0.5 rounded-full">
                            🏛️ BoT Mandatory Approval Tier (&gt;₦500k)
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-extrabold text-gray-900 mt-1">{r.title}</h4>
                      <p className="text-xs text-gray-500">{r.description}</p>
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-mono font-black text-red-700">₦{r.amount.toLocaleString()}</p>
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase mt-1 ${
                          r.status === 'disbursed'
                            ? 'bg-emerald-100 text-emerald-800'
                            : r.status === 'bot_approved'
                            ? 'bg-blue-100 text-blue-800'
                            : r.status === 'mc_reviewed'
                            ? 'bg-purple-100 text-purple-800'
                            : r.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {r.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Governance Approval Steps Indicator */}
                  <div className="p-3 bg-white rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white font-bold text-[10px] flex items-center justify-center">1</span>
                      <div>
                        <p className="font-bold text-gray-800">Submitted</p>
                        <p className="text-[10px] text-gray-500">{r.createdDate}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full font-bold text-[10px] flex items-center justify-center ${
                        r.status !== 'submitted' ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>2</span>
                      <div>
                        <p className="font-bold text-gray-800">MC Review</p>
                        <p className="text-[10px] text-gray-500">Management Committee</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full font-bold text-[10px] flex items-center justify-center ${
                        r.status === 'bot_approved' || r.status === 'disbursed' ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>3</span>
                      <div>
                        <p className="font-bold text-gray-800">BoT / Disbursement</p>
                        <p className="text-[10px] text-gray-500">{r.paymentVoucherNo || 'Pending PV'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions depending on Role & Status */}
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-200">
                    <button
                      onClick={() => setSelectedReqForDetail(r)}
                      className="text-xs font-bold text-[#0F5132] hover:underline cursor-pointer"
                    >
                      📜 View Full Approval History
                    </button>

                    <div className="flex items-center gap-2">
                      {r.status === 'submitted' && (
                        <button
                          onClick={() => handleAdvanceRequisition(r, 'mc_review')}
                          className="px-3 py-1.5 bg-purple-900 hover:bg-purple-800 text-amber-300 text-xs font-bold rounded-lg cursor-pointer shadow-sm"
                        >
                          ✓ MC Initial Clearance
                        </button>
                      )}

                      {(r.status === 'mc_reviewed' || (r.status === 'submitted' && !r.requiresBotApproval)) && (
                        <button
                          onClick={() => handleAdvanceRequisition(r, 'bot_approve')}
                          className="px-3 py-1.5 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 text-xs font-bold rounded-lg cursor-pointer shadow-sm"
                        >
                          👑 BoT Final Authorization
                        </button>
                      )}

                      {(r.status === 'bot_approved' || (r.status === 'mc_reviewed' && !r.requiresBotApproval)) && (
                        <button
                          onClick={() => handleAdvanceRequisition(r, 'disburse')}
                          className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-lg cursor-pointer shadow-sm"
                        >
                          💵 Disburse & Issue PV
                        </button>
                      )}

                      {r.status === 'disbursed' && (
                        <button
                          onClick={() => handlePrintPaymentVoucher(r)}
                          className="px-3 py-1.5 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 text-xs font-bold rounded-lg cursor-pointer shadow-sm flex items-center gap-1"
                        >
                          <span>🖨️</span> Print Official Payment Voucher
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB CONTENT: BUDGET VS ACTUAL */}
      {activeTab === 'budgets' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-base font-bold text-[#0F5132] font-serif">Departmental Budget vs. Actual Variance Analysis</h3>
              <p className="text-xs text-gray-500">Track approved allocations, actual cash spent, and committed requisitions</p>
            </div>
            <span className="text-xs font-mono font-bold bg-amber-100 text-amber-900 px-3 py-1 rounded-full">
              Total Allocation: ₦{store.budgets.reduce((s, b) => s + b.allocatedAmount, 0).toLocaleString()}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-100 text-gray-600 font-bold uppercase">
                <tr>
                  <th className="p-3">Dept Code</th>
                  <th className="p-3">Department Name</th>
                  <th className="p-3">Allocated Budget</th>
                  <th className="p-3">Actual Cash Spent</th>
                  <th className="p-3">Committed Requisitions</th>
                  <th className="p-3">Unexpended Variance</th>
                  <th className="p-3">% Consumed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-mono">
                {store.budgets.map(b => {
                  const unexpended = b.allocatedAmount - (b.spentAmount + b.committedAmount);
                  const pct = Math.round(((b.spentAmount + b.committedAmount) / b.allocatedAmount) * 100);

                  return (
                    <tr key={b.code} className="hover:bg-gray-50">
                      <td className="p-3 font-bold text-gray-700">{b.code}</td>
                      <td className="p-3 font-sans font-bold text-gray-900">{b.departmentName}</td>
                      <td className="p-3 text-gray-800 font-bold">₦{b.allocatedAmount.toLocaleString()}</td>
                      <td className="p-3 text-red-700 font-bold">₦{b.spentAmount.toLocaleString()}</td>
                      <td className="p-3 text-amber-700">₦{b.committedAmount.toLocaleString()}</td>
                      <td className={`p-3 font-bold ${unexpended >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                        ₦{unexpended.toLocaleString()}
                      </td>
                      <td className="p-3 font-sans">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            pct > 90 ? 'bg-red-100 text-red-800' : pct > 60 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {pct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: FINANCIAL STATEMENTS & AUDIT DISCLOSURES */}
      {activeTab === 'statements' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-6">
          <div className="flex justify-between items-center border-b border-gray-200 pb-4">
            <div>
              <h3 className="text-lg font-bold text-[#0F5132] font-serif">Independent Audit & Public Disclosure Portal</h3>
              <p className="text-xs text-gray-500">Complete Financial Position, Surplus/Deficit, and Cash Flow Statement for Royal Traditional Council</p>
            </div>
            <button
              onClick={handlePrintAuditReport}
              className="px-4 py-2 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 font-bold rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <span>🖨️</span> Download / Print Full Audit Statement
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Statement of Income & Expenditure */}
            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-3">
              <h4 className="text-sm font-extrabold text-[#0F5132] border-b border-gray-200 pb-2">
                1. Statement of Income & Expenditure (NPO Surplus/Deficit)
              </h4>
              <div className="space-y-2 text-xs font-mono">
                <div className="flex justify-between text-emerald-800 font-bold">
                  <span>Gross Philanthropic Revenue & Sales:</span>
                  <span>₦{totalRevenues.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-red-700 font-bold">
                  <span>Total Festival Expenses:</span>
                  <span>-₦{totalExpenses.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-amber-800 font-black border-t-2 border-gray-300 pt-2 text-sm">
                  <span>Net Operating Surplus (Carried to Reserve):</span>
                  <span>₦{netSurplus.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Statement of Financial Position */}
            <div className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-3">
              <h4 className="text-sm font-extrabold text-[#0F5132] border-b border-gray-200 pb-2">
                2. Statement of Financial Position (Balance Sheet)
              </h4>
              <div className="space-y-2 text-xs font-mono">
                <div className="flex justify-between text-gray-800 font-bold">
                  <span>Liquid Cash & Bank Reserves:</span>
                  <span>₦{netSurplus.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-indigo-800 font-bold">
                  <span>Outstanding Pledges Receivable:</span>
                  <span>₦{outstandingPledges.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-teal-800 font-bold">
                  <span>Capital Equipment & Assets:</span>
                  <span>₦{store.assets.reduce((s, a) => s + a.value, 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-[#0F5132] font-black border-t-2 border-gray-300 pt-2 text-sm">
                  <span>TOTAL NET ASSETS & FUND RESERVES:</span>
                  <span>₦{(totalAssetsValue + outstandingPledges).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: GOVERNANCE & COMPLIANCE FRAMEWORK */}
      {activeTab === 'governance' && (
        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-6">
          <div className="border-b border-gray-200 pb-3">
            <h3 className="text-lg font-bold text-[#0F5132] font-serif">2-Tier Governance & Institutional Policy Framework</h3>
            <p className="text-xs text-gray-500">Uzairue Kingdom Festival Central Steering Committee & Royal Foundation Operating Manual</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-gray-700 leading-relaxed">
            <div className="p-5 bg-amber-50/60 rounded-2xl border border-amber-200 space-y-2">
              <h4 className="font-extrabold text-amber-900 text-sm flex items-center gap-2">
                <span>🏛️</span> Board of Trustees (BoT) Mandate
              </h4>
              <p>Chaired by HRH Ogieneni of Uzairue Kingdom and High Chiefs.</p>
              <ul className="list-disc pl-4 space-y-1 text-amber-950 font-medium">
                <li>High-tier approval for requisitions exceeding ₦500,000 threshold</li>
                <li>Annual audit sign-off & public financial disclosures</li>
                <li>Oversight of Uzairue Youth Skill & Healthcare Endowment Fund</li>
                <li>Dual-signatory mandate on all primary First Bank & Zenith accounts</li>
              </ul>
            </div>

            <div className="p-5 bg-emerald-50/60 rounded-2xl border border-emerald-200 space-y-2">
              <h4 className="font-extrabold text-[#0F5132] text-sm flex items-center gap-2">
                <span>⚙️</span> Management Committee (MC) Mandate
              </h4>
              <p>Led by Executive Committee Chairman, Treasurer, and Financial Scribe.</p>
              <ul className="list-disc pl-4 space-y-1 text-emerald-950 font-medium">
                <li>Day-to-day festival planning, vendor contracting & stage logistics</li>
                <li>Operational requisitions under ₦500,000 threshold</li>
                <li>Pledge retirement documentation & official receipt issuance</li>
                <li>Real-time ERUEWin Raffle Hub sales reconciliation</li>
              </ul>
            </div>
          </div>

          {/* Master Chart of Accounts Table */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-[#0F5132]">Master Chart of Accounts (CAMA 2020 / IPSAS NPO Architecture)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-100 text-gray-600 font-bold uppercase">
                  <tr>
                    <th className="p-2.5">Code</th>
                    <th className="p-2.5">Account Name</th>
                    <th className="p-2.5">Type</th>
                    <th className="p-2.5">Description & Usage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 font-mono">
                  {CHART_OF_ACCOUNTS.map(a => (
                    <tr key={a.code} className="hover:bg-gray-50">
                      <td className="p-2.5 font-bold text-[#0F5132]">{a.code}</td>
                      <td className="p-2.5 font-sans font-bold text-gray-900">{a.name}</td>
                      <td className="p-2.5 font-sans uppercase text-[10px] font-black">{a.type}</td>
                      <td className="p-2.5 font-sans text-gray-500 text-[11px]">{a.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: RECORD TRANSACTION */}
      {isNewTxModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-amber-400">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-[#0F5132]">Record General Ledger Entry</h3>
              <button onClick={() => setIsNewTxModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleCreateTransaction} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Transaction Date</label>
                  <input
                    type="date"
                    value={newTx.date}
                    onChange={e => setNewTx({ ...newTx, date: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700">Transaction Type</label>
                  <select
                    value={newTx.type}
                    onChange={e => setNewTx({ ...newTx, type: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white font-bold"
                  >
                    <option value="revenue">Revenue</option>
                    <option value="expense">Expense</option>
                    <option value="donation">Donation</option>
                    <option value="pledge_redemption">Pledge Redemption</option>
                    <option value="store_sale">Store Sale</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700">Account Code & Classification</label>
                <select
                  value={newTx.accountCode}
                  onChange={e => setNewTx({ ...newTx, accountCode: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white font-mono text-xs"
                >
                  {CHART_OF_ACCOUNTS.map(a => (
                    <option key={a.code} value={a.code}>
                      {a.code} - {a.name} ({a.type.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-bold text-gray-700">Description / Justification</label>
                <input
                  type="text"
                  value={newTx.description}
                  onChange={e => setNewTx({ ...newTx, description: e.target.value })}
                  placeholder="e.g. Donation by Royal Chief / Stage Timber Procurement"
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Amount (₦)</label>
                  <input
                    type="number"
                    value={newTx.amount}
                    onChange={e => setNewTx({ ...newTx, amount: e.target.value })}
                    placeholder="e.g. 500000"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 font-mono font-bold"
                    required
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700">Payer / Payee Name</label>
                  <input
                    type="text"
                    value={newTx.payerOrPayee}
                    onChange={e => setNewTx({ ...newTx, payerOrPayee: e.target.value })}
                    placeholder="e.g. Chief Aliu / Vendor Co."
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Payment Channel</label>
                  <select
                    value={newTx.paymentMethod}
                    onChange={e => setNewTx({ ...newTx, paymentMethod: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="paystack_card">Paystack Online</option>
                    <option value="cash">Petty Cash</option>
                    <option value="pos">POS Terminal</option>
                    <option value="cheque">Bank Cheque</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-gray-700">Bank Ref / Receipt Ref</label>
                  <input
                    type="text"
                    value={newTx.referenceNo}
                    onChange={e => setNewTx({ ...newTx, referenceNo: e.target.value })}
                    placeholder="e.g. FBN-TRF-9012"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 font-mono"
                  />
                </div>
              </div>

              <div className="pt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewTxModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0F5132] text-amber-300 rounded-xl font-bold cursor-pointer shadow-md"
                >
                  Save Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: REGISTER NEW PLEDGE */}
      {isNewPledgeModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-amber-400">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-[#0F5132]">Register Philanthropic / Royal Pledge</h3>
              <button onClick={() => setIsNewPledgeModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleCreatePledge} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-gray-700">Pledger Name / Organization</label>
                <input
                  type="text"
                  value={newPledge.pledgerName}
                  onChange={e => setNewPledge({ ...newPledge, pledgerName: e.target.value })}
                  placeholder="e.g. Uzairue Diaspora UK Chapter / Chief Raymond"
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Title / Office</label>
                  <input
                    type="text"
                    value={newPledge.titleOrOffice}
                    onChange={e => setNewPledge({ ...newPledge, titleOrOffice: e.target.value })}
                    placeholder="e.g. High Chief / Chapter Lead"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700">Village / Diaspora Chapter</label>
                  <input
                    type="text"
                    value={newPledge.villageOrChapter}
                    onChange={e => setNewPledge({ ...newPledge, villageOrChapter: e.target.value })}
                    placeholder="e.g. Jattu-Uzairue or USA Chapter"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Pledger Category</label>
                  <select
                    value={newPledge.pledgerType}
                    onChange={e => setNewPledge({ ...newPledge, pledgerType: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white font-bold"
                  >
                    <option value="royal_chief">Royal Chief / Elder</option>
                    <option value="diaspora_chapter">Diaspora Chapter</option>
                    <option value="corporate_sponsor">Corporate Sponsor</option>
                    <option value="individual">Individual Son/Daughter</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-gray-700">Amount Pledged (₦)</label>
                  <input
                    type="number"
                    value={newPledge.amountPledged}
                    onChange={e => setNewPledge({ ...newPledge, amountPledged: e.target.value })}
                    placeholder="e.g. 1000000"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 font-mono font-bold"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700">Target Purpose / Designated Project</label>
                <input
                  type="text"
                  value={newPledge.purpose}
                  onChange={e => setNewPledge({ ...newPledge, purpose: e.target.value })}
                  placeholder="e.g. Stage construction or 22 Village Troupes Grant"
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                />
              </div>

              <div className="pt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewPledgeModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0F5132] text-amber-300 rounded-xl font-bold cursor-pointer shadow-md"
                >
                  Register Commitment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: RETIRE / FULFILL PLEDGE */}
      {isRetirePledgeModalOpen && selectedPledgeToRetire && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl border border-amber-400">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-[#0F5132]">Fulfill / Retire Pledge Commitment</h3>
              <button onClick={() => setIsRetirePledgeModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
            </div>

            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-xs space-y-1">
              <p className="font-bold text-amber-900">{selectedPledgeToRetire.pledgerName}</p>
              <p className="text-amber-800 font-mono">
                Total Pledged: ₦{selectedPledgeToRetire.amountPledged.toLocaleString()} | Outstanding: ₦{(selectedPledgeToRetire.amountPledged - selectedPledgeToRetire.amountRedeemed).toLocaleString()}
              </p>
            </div>

            <form onSubmit={handleRetirePledge} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-gray-700">Redemption Amount (₦)</label>
                <input
                  type="number"
                  value={retireAmount}
                  onChange={e => setRetireAmount(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-xl font-mono font-bold text-base mt-1"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-gray-700">Payment Channel / Method</label>
                <select
                  value={retireMethod}
                  onChange={e => setRetireMethod(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white"
                >
                  <option value="Bank Wire Transfer">Bank Wire Transfer</option>
                  <option value="First Bank Direct Deposit">First Bank Direct Deposit</option>
                  <option value="Paystack Online Gateway">Paystack Online Gateway</option>
                  <option value="Petty Cash Vault">Petty Cash Vault</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-gray-700">Bank Transaction / Wire Reference</label>
                <input
                  type="text"
                  value={retireBankRef}
                  onChange={e => setRetireBankRef(e.target.value)}
                  placeholder="e.g. FBN-DEP-993021"
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1 font-mono"
                />
              </div>

              <div className="pt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsRetirePledgeModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold cursor-pointer shadow-md"
                >
                  Confirm Retirement & Issue Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: SUBMIT EXPENDITURE REQUISITION */}
      {isNewReqModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-amber-400">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-base font-bold text-[#0F5132]">Submit Expenditure Requisition</h3>
              <button onClick={() => setIsNewReqModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleCreateRequisition} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-gray-700">Requisition Title</label>
                <input
                  type="text"
                  value={newReq.title}
                  onChange={e => setNewReq({ ...newReq, title: e.target.value })}
                  placeholder="e.g. Stage Construction / 120 Vigilantes Stipends"
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Department</label>
                  <select
                    value={newReq.department}
                    onChange={e => setNewReq({ ...newReq, department: e.target.value as any })}
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white font-bold"
                  >
                    <option value="Logistics & Stage">Logistics & Stage</option>
                    <option value="Cultural Troupes">Cultural Troupes</option>
                    <option value="Security & Health">Security & Health</option>
                    <option value="Media & Publicity">Media & Publicity</option>
                    <option value="Royal Protocol">Royal Protocol</option>
                    <option value="Raffle Admin">Raffle Admin</option>
                    <option value="General Secretarial">General Secretarial</option>
                  </select>
                </div>

                <div>
                  <label className="font-bold text-gray-700">Amount (₦)</label>
                  <input
                    type="number"
                    value={newReq.amount}
                    onChange={e => setNewReq({ ...newReq, amount: e.target.value })}
                    placeholder="e.g. 1200000"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 font-mono font-bold"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700">Account Code Allocation</label>
                <select
                  value={newReq.accountCode}
                  onChange={e => setNewReq({ ...newReq, accountCode: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1 bg-white font-mono text-xs"
                >
                  {CHART_OF_ACCOUNTS.filter(a => a.type === 'expense').map(a => (
                    <option key={a.code} value={a.code}>
                      {a.code} - {a.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-bold text-gray-700">Detailed Description & Justification</label>
                <textarea
                  value={newReq.description}
                  onChange={e => setNewReq({ ...newReq, description: e.target.value })}
                  placeholder="Explain requirement, quantities, and vendor details..."
                  className="w-full p-2 border border-gray-300 rounded-xl mt-1 h-20"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-gray-700">Beneficiary / Contractor</label>
                  <input
                    type="text"
                    value={newReq.beneficiaryName}
                    onChange={e => setNewReq({ ...newReq, beneficiaryName: e.target.value })}
                    placeholder="e.g. Royal Sound Services Ltd"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1"
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700">Bank Account Details</label>
                  <input
                    type="text"
                    value={newReq.bankAccountDetails}
                    onChange={e => setNewReq({ ...newReq, bankAccountDetails: e.target.value })}
                    placeholder="e.g. First Bank - 3029102931"
                    className="w-full p-2 border border-gray-300 rounded-xl mt-1 font-mono"
                  />
                </div>
              </div>

              <div className="pt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewReqModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0F5132] text-amber-300 rounded-xl font-bold cursor-pointer shadow-md"
                >
                  Submit for Governance Approval
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: REQUISITION AUDIT TRAIL DETAIL */}
      {selectedReqForDetail && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 space-y-4 shadow-2xl border border-amber-400 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <div>
                <span className="font-mono text-xs font-bold bg-[#0F5132] text-amber-300 px-2 py-0.5 rounded">
                  {selectedReqForDetail.requisitionNo}
                </span>
                <h3 className="text-base font-bold text-gray-900 mt-1">{selectedReqForDetail.title}</h3>
              </div>
              <button onClick={() => setSelectedReqForDetail(null)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
            </div>

            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 text-xs space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><strong className="text-gray-500">Department:</strong> {selectedReqForDetail.department}</div>
                <div><strong className="text-gray-500">Amount:</strong> ₦{selectedReqForDetail.amount.toLocaleString()}</div>
                <div><strong className="text-gray-500">Requested By:</strong> {selectedReqForDetail.requestedBy}</div>
                <div><strong className="text-gray-500">Status:</strong> {selectedReqForDetail.status.toUpperCase()}</div>
                <div><strong className="text-gray-500">Payee:</strong> {selectedReqForDetail.beneficiaryName || 'N/A'}</div>
                <div><strong className="text-gray-500">Payment Voucher:</strong> {selectedReqForDetail.paymentVoucherNo || 'N/A'}</div>
              </div>
              <p className="text-gray-700 italic border-t border-gray-200 pt-2">{selectedReqForDetail.description}</p>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-extrabold text-[#0F5132] uppercase">Governance Authorization Trail:</h4>
              <div className="space-y-2">
                {selectedReqForDetail.approvalHistory.map(h => (
                  <div key={h.id} className="p-3 bg-white rounded-xl border border-gray-200 text-xs space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-[#0F5132]">{h.step} &bull; {h.action.toUpperCase()}</span>
                      <span className="text-[10px] text-gray-400 font-mono">{h.timestamp}</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{h.actorName} ({h.actorRole})</p>
                    {h.notes && <p className="text-gray-500 text-[11px]">{h.notes}</p>}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-3 flex justify-between items-center border-t border-gray-100">
              {selectedReqForDetail.status === 'disbursed' ? (
                <button
                  onClick={() => handlePrintPaymentVoucher(selectedReqForDetail)}
                  className="px-4 py-2 bg-[#0F5132] text-amber-300 rounded-xl font-bold text-xs shadow-md cursor-pointer flex items-center gap-1.5"
                >
                  <span>🖨️</span> Print Official Payment Voucher
                </button>
              ) : <span />}

              <button
                onClick={() => setSelectedReqForDetail(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold text-xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
